import { useEffect } from 'react'
import { useDataRefresh, useFetch } from '../hooks'
import { GET_USER_INFO } from '../graphql'
import { FetchState, StatusType } from '../hooks/fetch-state/types'
import { UserQueryType } from '../types/types'
import { createContextFactory } from './utils/context-factory'
import { AdminToolWrapperProps, LangValue } from '../components'
import { ErrorProvider } from './error.context'
import { useTranslationData } from '.'

/**
 * User info context.
 */

export interface UserData {
  refreshUserData: () => void
  userState: FetchState<UserQueryType>
}

const [useUserData, UserContextProvider] = createContextFactory<UserData>(
  'useUserData',
  'UserContextProvider'
)

/**
 * Provider for user info.
 * @param {ReactNode} children
 */
const UserInfoProvider = ({
  children,
  useAdminTool
}: AdminToolWrapperProps) => {
  const { UILanguage, UILanguageUpdated, selectUILanguage } =
    useTranslationData()

  const { fetchState, execute } = useFetch<UserQueryType>({
    query: GET_USER_INFO
  })

  const setShouldRefresh = useDataRefresh(execute)

  useEffect(() => {
    if (
      fetchState.status === StatusType.FETCHED &&
      fetchState.data &&
      fetchState.data.userInfo?.asiointikieli !== UILanguage &&
      !UILanguageUpdated
    ) {
      selectUILanguage(fetchState.data.userInfo?.asiointikieli as LangValue)
    }
  }, [UILanguage, UILanguageUpdated, fetchState, selectUILanguage])

  return (
    <ErrorProvider useAdminTool={useAdminTool}>
      <UserContextProvider
        value={{
          refreshUserData: () => setShouldRefresh(true),
          userState: fetchState
        }}
      >
        {children}
      </UserContextProvider>
    </ErrorProvider>
  )
}

export { UserInfoProvider, useUserData }
